import { startTransactionLive } from '@/lib/api/transactionLive';
import { clearQueryCache, refreshCachedQueries } from '@/lib/api/queryCache';
import { authApi, Organization, User } from '@/lib/api/auth';
import { SESSION_EXPIRED_EVENT } from '@/lib/api/fetchClient';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  AuthContext,
  AuthContextType,
  RegisterRequest,
} from './AuthContextInstance';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;
  const userId = user?.id;
  useEffect(() => {
    if (!userId) {
      clearQueryCache();
      return;
    }
    const live = startTransactionLive();
    const poll = () => refreshCachedQueries(live.connected);
    const refreshOnFocus = () => refreshCachedQueries();
    const timer = window.setInterval(poll, 60_000);
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'auth_token' || event.key === null) clearQueryCache();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      live.stop();
      clearInterval(timer);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
      window.removeEventListener('storage', onStorage);
      clearQueryCache();
    };
  }, [userId]);

  const login = async (identifier: string, password: string) => {
    try {
      const response = await authApi.login({
        identifier: identifier.trim(),
        password,
      });

      if (response.success) {
        setUser(response.user);
        setOrganization(response.organization || null);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const demoLogin = async () => {
    const response = await authApi.demoLogin();

    if (response.success) {
      setUser(response.user);
      setOrganization(response.organization || null);
    } else {
      throw new Error(response.message);
    }
  };

  const googleLogin = useCallback(
    async (credential: string, currentPassword?: string) => {
      const response = await authApi.googleLogin(credential, currentPassword);
      if (!response.success) throw new Error(response.message);
      setUser(response.user);
      setOrganization(response.organization || null);
      return response.is_new_user;
    },
    []
  );

  const register = async (data: RegisterRequest) => {
    // Call backend register endpoint
    const res = await authApi.register({
      username: data.username,
      email: data.email,
      password: data.password,
    });

    if (!res.success) {
      throw new Error(res.message || 'Registration failed');
    }

    // Auto-login after successful registration
    await login(data.email, data.password);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setOrganization(null);
    }
  };

  const refreshUser = async () => {
    if (!authApi.isAuthenticated()) {
      setUser(null);
      setOrganization(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.getProfile();
      if (response.success) {
        setUser(response.user);
        setOrganization(response.organization || null);
      } else {
        // If profile fetch fails, user might not be authenticated
        setUser(null);
        setOrganization(null);
      }
    } catch {
      // If profile fetch fails, clear user data
      setUser(null);
      setOrganization(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      if (authApi.isAuthenticated()) {
        await refreshUser();
      } else {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Log user out when any API call returns 401 (session expired)
  useEffect(() => {
    const handleSessionExpired = () => {
      authApi.setToken(null);
      setUser(null);
      setOrganization(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const value: AuthContextType = {
    user,
    organization,
    isAuthenticated,
    isLoading,
    login,
    googleLogin,
    demoLogin,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
